import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MapPin, Calendar, Users, FileText, Film, Image as ImageIcon, ArrowRight } from "lucide-react";
import { PageHero } from "@/pages/About";
import AnimatedCounter from "@/components/AnimatedCounter";
import { api, BACKEND_URL } from "@/lib/api";

export const CATEGORY_LABELS = {
    food_assistance: "Food Assistance",
    youth_empowerment: "Youth Empowerment",
    women_empowerment: "Women Empowerment",
    disability_support: "Disability Support",
    elderly_support: "Elderly Support",
    skills_development: "Skills Development",
    health: "Health Programmes",
    education: "Education Support",
    entrepreneurship: "Entrepreneurship",
    warren_park_connect: "Warren Park Connect",
    environmental: "Environmental Projects",
    community_infrastructure: "Community Infrastructure",
    emergency_relief: "Emergency Relief",
    volunteer_activities: "Volunteer Activities",
};

export function resolveMediaUrl(u) {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
    return u;
}

export default function ImpactHub() {
    const [projects, setProjects] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [stats, setStats] = useState([]);
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const q = new URLSearchParams();
        if (category) q.set("category", category);
        Promise.all([
            api.get(`/public/projects?${q.toString()}`).then((r) => r.data),
            api.get("/public/timeline").then((r) => r.data),
            api.get("/public/impact-stats").then((r) => r.data),
        ]).then(([p, t, s]) => {
            setProjects(p.items || []);
            setTimeline(t.years || []);
            setStats(s.stats || []);
        }).finally(() => setLoading(false));
    }, [category]);

    const featured = useMemo(() => projects.find((p) => p.featured) || projects[0], [projects]);

    return (
        <div data-testid="impact-hub">
            <section className="relative bg-[color:var(--gsf-green-900)] text-white overflow-hidden" data-testid="hub-hero">
                <div className="absolute inset-0 opacity-30 grain" />
                <div className="relative gsf-container py-20 sm:py-28">
                    <div className="max-w-3xl">
                        <div className="gsf-eyebrow text-[color:var(--gsf-gold-500)]">Impact Hub</div>
                        <h1 className="mt-3 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                            Creating Lasting Community Impact
                        </h1>
                        <p className="mt-5 text-lg text-white/85 max-w-2xl leading-relaxed">
                            Every project tells a story. Every life changed represents measurable community transformation.
                        </p>
                    </div>
                </div>
            </section>

            {stats.length > 0 && (
                <section className="bg-[color:var(--gsf-green-800)] text-white py-14" data-testid="hub-stats">
                    <div className="gsf-container grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                        {stats.map((s) => (
                            <div key={s.key} className="text-center" data-testid={`hub-stat-${s.key}`}>
                                <div className="font-serif text-3xl sm:text-4xl font-bold text-[color:var(--gsf-gold-500)]">
                                    <AnimatedCounter value={s.value} prefix={s.prefix} suffix={s.suffix} />
                                </div>
                                <div className="mt-2 text-xs text-white/70 leading-snug">{s.title || s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="gsf-section" data-testid="hub-filters-projects">
                <div className="gsf-container">
                    <div className="flex flex-wrap gap-2 mb-8" data-testid="hub-category-filters">
                        <button onClick={() => setParams({})} data-testid="filter-all"
                            className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${!category ? "bg-[color:var(--gsf-green-700)] text-white" : "bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)] hover:bg-[color:var(--gsf-green-700)]/10"}`}>
                            All projects
                        </button>
                        {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                            <button key={k} onClick={() => setParams({ category: k })} data-testid={`filter-${k}`}
                                className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${category === k ? "bg-[color:var(--gsf-green-700)] text-white" : "bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)] hover:bg-[color:var(--gsf-green-700)]/10"}`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <p className="text-gray-500">Loading projects…</p>
                    ) : projects.length === 0 ? (
                        <div className="gsf-card p-8 max-w-2xl" data-testid="hub-empty">
                            <div className="gsf-eyebrow">Coming soon</div>
                            <h3 className="mt-2 font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">
                                Projects for this category will appear here as they are published.
                            </h3>
                            <p className="mt-3 text-sm text-[color:var(--gsf-body)]">Administrators can publish verified projects via the admin dashboard.</p>
                        </div>
                    ) : (
                        <>
                            {featured && !category && <FeaturedProject project={featured} />}
                            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {projects.map((p) => <ProjectCard key={p.slug} project={p} />)}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {timeline.length > 0 && (
                <section className="bg-[color:var(--gsf-soft-green)] gsf-section" data-testid="hub-timeline">
                    <div className="gsf-container">
                        <div className="gsf-eyebrow">Impact Timeline</div>
                        <h2 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Community impact by year</h2>
                        <div className="mt-8 space-y-8">
                            {timeline.map((y) => (
                                <div key={y.year} data-testid={`timeline-${y.year}`}>
                                    <h3 className="font-serif text-2xl text-[color:var(--gsf-green-700)] font-semibold border-b border-[color:var(--gsf-green-600)]/30 pb-2">
                                        {y.year}
                                    </h3>
                                    <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {y.items.map((it) => (
                                            <li key={it.slug}>
                                                <Link to={`/impact-hub/${it.slug}`} className="flex items-center gap-3 group" data-testid={`timeline-item-${it.slug}`}>
                                                    {it.images?.[0] && <img src={resolveMediaUrl(it.images[0])} alt={it.title} className="w-14 h-14 rounded-lg object-cover" />}
                                                    <div>
                                                        <div className="text-sm font-medium text-[color:var(--gsf-green-700)] group-hover:underline">{it.title}</div>
                                                        <div className="text-xs text-gray-500">{CATEGORY_LABELS[it.category] || it.category}</div>
                                                    </div>
                                                    {it.verified && <CheckCircle2 size={14} className="ml-auto text-green-600" />}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}

function FeaturedProject({ project }) {
    const hero = project.images?.[0];
    return (
        <Link to={`/impact-hub/${project.slug}`} className="gsf-card overflow-hidden grid md:grid-cols-2 gap-0 mb-6" data-testid={`featured-${project.slug}`}>
            <div className="aspect-video md:aspect-auto bg-[color:var(--gsf-soft-green)]">
                {hero ? <img src={resolveMediaUrl(hero)} alt={project.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[color:var(--gsf-green-600)]"><ImageIcon size={48} /></div>}
            </div>
            <div className="p-8">
                <div className="gsf-eyebrow">Featured project</div>
                <h3 className="mt-2 font-serif text-2xl text-[color:var(--gsf-green-700)] font-bold">{project.title}</h3>
                {project.summary && <p className="mt-3 text-[color:var(--gsf-body)] leading-relaxed">{project.summary}</p>}
                <div className="mt-4 flex items-center gap-3 text-xs text-gray-500">
                    {project.verified && <span className="flex items-center gap-1 text-green-700"><CheckCircle2 size={12} /> Verified</span>}
                    {project.location_name && <span className="flex items-center gap-1"><MapPin size={12} /> {project.location_name}</span>}
                    {project.completion_date && <span className="flex items-center gap-1"><Calendar size={12} /> {project.completion_date}</span>}
                </div>
                <div className="mt-5 gsf-btn-outline text-xs">Read full story <ArrowRight size={14} /></div>
            </div>
        </Link>
    );
}

export function ProjectCard({ project }) {
    const hero = project.images?.[0];
    return (
        <Link to={`/impact-hub/${project.slug}`} className="gsf-card overflow-hidden flex flex-col" data-testid={`project-card-${project.slug}`}>
            <div className="aspect-video bg-[color:var(--gsf-soft-green)]">
                {hero ? <img src={resolveMediaUrl(hero)} alt={project.title} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-[color:var(--gsf-green-600)]"><ImageIcon size={40} /></div>}
            </div>
            <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--gsf-green-600)]">{CATEGORY_LABELS[project.category] || project.category || "Project"}</span>
                    {project.verified && <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700"><CheckCircle2 size={10} /> Verified</span>}
                </div>
                <h3 className="mt-2 font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold leading-snug">{project.title}</h3>
                {project.summary && <p className="mt-2 text-sm text-[color:var(--gsf-body)] leading-relaxed line-clamp-3">{project.summary}</p>}
                <div className="mt-auto pt-4 flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
                    {project.location_name && <span className="flex items-center gap-1"><MapPin size={11} /> {project.location_name}</span>}
                    {project.beneficiaries_count > 0 && <span className="flex items-center gap-1"><Users size={11} /> {project.beneficiaries_count.toLocaleString()}</span>}
                    {(project.images?.length || 0) > 0 && <span className="flex items-center gap-1"><ImageIcon size={11} /> {project.images.length}</span>}
                    {(project.videos?.length || 0) > 0 && <span className="flex items-center gap-1"><Film size={11} /> {project.videos.length}</span>}
                    {(project.documents?.length || 0) > 0 && <span className="flex items-center gap-1"><FileText size={11} /> {project.documents.length}</span>}
                </div>
            </div>
        </Link>
    );
}

export { PageHero };
